import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material'
import { CurrencyCode, CurrencySymbols } from 'api/models/Common/JyrneyEnums'
import { CountrySelect } from 'components/Common/CountrySelect/CountrySelect'
import MuiPhoneForm from 'components/Common/Phone/muiPhoneForm'
import TimeZoneSelect from 'components/Common/TimeZoneSelect/TimeZoneSelect'
import RadioSwitch from 'components/FormFields/RadioSwitch/RadioSwitch'
import { useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

export function BillingDetails({ ...props }) {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext()

  const sameAs = watch('sameAsCompanyAddress')
  const companyAddress = watch('address')
  const vatRegistered = watch('billing.vatRegistered')
  const billingTermTypeName = watch('billing.billingTermTypeName')
  const isBilledFromParent = watch('settings.isBilledFromParent')
  console.log('formContext', props.contextFields)
  console.log('errors', errors)

  useEffect(() => {
    if (sameAs) {
      setValue('billing.address.addressLine1', companyAddress.addressLine1, {
        shouldDirty: true,
        shouldTouch: true,
      })
      setValue('billing.address.addressLine2', companyAddress.addressLine, {
        shouldDirty: true,
        shouldTouch: true,
      })
      setValue('billing.address.town', companyAddress.town, {
        shouldDirty: true,
        shouldTouch: true,
      })
      setValue('billing.address.county', companyAddress.county, {
        shouldDirty: true,
        shouldTouch: true,
      })
      setValue('billing.address.postCode', companyAddress.postCode, {
        shouldDirty: true,
        shouldTouch: true,
      })
      setValue('billing.address.country', companyAddress.country, {
        shouldDirty: true,
        shouldTouch: true,
      })
      setValue('billing.address.countryCode', companyAddress.countryCode, {
        shouldDirty: true,
        shouldTouch: true,
      })
    } else {
      setValue('billing.address', {
        addressLine1: '',
        addressLine2: '',
        town: '',
        county: '',
        postCode: '',
        country: '',
        countryCode: '',
      })
    }
  }, [sameAs, companyAddress])

  useEffect(() => {
    if (!vatRegistered) {
      setValue('billing.vatNumber', '')
    }
  }, [vatRegistered])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        width: '80%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '20px',
          flexDirection: 'row',
          paddingTop: '15px',
        }}
      ></Box>
      {!isBilledFromParent && (
        <Box sx={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
          <Controller
            name="billing.entity"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                sx={{ flexGrow: 0.3 }}
                label={'Billing Entity Name'}
              />
            )}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Typography>Billing Address</Typography>
            <Controller
              name="sameAsCompanyAddress"
              control={control}
              render={({ field }) => (
                <>
                  {companyAddress && (
                    <FormControlLabel
                      control={
                        <Checkbox disabled={!companyAddress} {...field} />
                      }
                      sx={{
                        display:
                          !!errors.address && !!(errors.address as any)
                            ? 'none'
                            : 'block',
                      }}
                      label="Same as Company Address"
                    />
                  )}
                </>
              )}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: '10px' }}>
            <Controller
              name="billing.address.addressLine1"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  disabled={sameAs}
                  sx={{ flexGrow: 0.3 }}
                  label={`Street Address`}
                  InputLabelProps={{ shrink: !!sameAs || field.value }}
                />
              )}
            />

            <Controller
              name="billing.address.addressLine2"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  disabled={sameAs}
                  sx={{ flexGrow: 1 }}
                  label={'Street Address'}
                  InputLabelProps={{ shrink: !!sameAs || field.value }}
                />
              )}
            />
          </Box>

          <Controller
            name="billing.address.town"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={sameAs}
                sx={{ flexGrow: 1 }}
                label={'Town / City'}
                InputLabelProps={{ shrink: !!sameAs || field.value }}
              />
            )}
          />
          <Box sx={{ display: 'flex', gap: '10px' }}>
            <Controller
              name="billing.address.county"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  sx={{ flexGrow: 1 }}
                  disabled={sameAs}
                  label={'County'}
                  InputLabelProps={{ shrink: !!sameAs || field.value }}
                />
              )}
            />
            <Controller
              name="billing.address.postCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  disabled={sameAs}
                  sx={{ flexGrow: 0.3 }}
                  label={
                    props.companyLocation === 'GBR'
                      ? 'Post Code'
                      : 'Postal / Zip code'
                  }
                  InputLabelProps={{ shrink: !!sameAs || field.value }}
                />
              )}
            />
          </Box>
          <Box sx={{ gap: '10px', display: 'none' }}>
            <Controller
              name="billing.address.country"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  sx={{ flexGrow: 1 }}
                  disabled={sameAs}
                  label={'Country'}
                  InputLabelProps={{ shrink: !!sameAs || field.value }}
                />
              )}
            />
          </Box>
          <Box sx={{ width: '100%' }}>
            <Controller
              name="billing.address.countryCode"
              control={control}
              render={({ field }) => (
                <CountrySelect
                  {...field}
                  {...props}
                  sibling="billing.address.country"
                />
              )}
            />
          </Box>
          <Typography>Billing Contact</Typography>
          <Box sx={{ display: 'flex', gap: '10px' }}>
            <Controller
              name="billing.contact.name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  sx={{ flexGrow: 0.5 }}
                  label={'Contact Name'}
                  required={
                    !!errors.billing &&
                    typeof errors.billing === 'object' &&
                    'contact' in errors.billing &&
                    !!(errors.billing as any).contact?.name
                  }
                  error={
                    !!errors.billing &&
                    typeof errors.billing === 'object' &&
                    'contact' in errors.billing &&
                    !!(errors.billing as any).contact?.name
                  }
                  helperText={
                    errors.billing &&
                    typeof errors.billing === 'object' &&
                    'contact' in errors.billing &&
                    (errors.billing as any).contact?.name?.message
                  }
                />
              )}
            />
            <Controller
              name="billing.contact.email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  sx={{ flexGrow: 0.5 }}
                  label={'Email Address'}
                  required={
                    !!errors.billing &&
                    typeof errors.billing === 'object' &&
                    'contact' in errors.billing &&
                    !!(errors.billing as any).contact?.email
                  }
                  error={
                    !!errors.billing &&
                    typeof errors.billing === 'object' &&
                    'contact' in errors.billing &&
                    !!(errors.billing as any).contact?.email
                  }
                  helperText={
                    errors.billing &&
                    typeof errors.billing === 'object' &&
                    'contact' in errors.billing &&
                    (errors.billing as any).contact?.email?.message
                  }
                />
              )}
            />
            <Controller
              name="billing.contact.contactNumber"
              control={control}
              render={({ field }) => (
                <>
                  <MuiPhoneForm
                    {...field}
                    label={'Phone Number'}
                    fullWidth
                    required={
                      !!errors.billing &&
                      typeof errors.billing === 'object' &&
                      'contact' in errors.billing &&
                      !!(errors.billing as any).contact?.contactNumber
                    }
                    error={
                      !!errors.billing &&
                      typeof errors.billing === 'object' &&
                      'contact' in errors.billing &&
                      !!(errors.billing as any).contact?.contactNumber
                    }
                    helperText={
                      errors.billing &&
                      typeof errors.billing === 'object' &&
                      'contact' in errors.billing &&
                      (errors.billing as any).contact?.contactNumber?.message
                    }
                  />
                </>
              )}
            />
          </Box>
          <Typography>Locale Settings</Typography>

          <Box sx={{ display: 'flex', gap: '10px' }}>
            <Controller
              name="timezone"
              control={control}
              render={({ field }) => (
                <TimeZoneSelect
                  {...field}
                  sx={{ flexGrow: 0.3 }}
                  label={'Time zone'}
                  disabled={true}
                />
              )}
            />
            <Controller
              name="currency"
              control={control}
              render={({ field }) => {
                return (
                  <FormControl sx={{ width: '200px' }}>
                    <InputLabel id="currency">Currency</InputLabel>
                    <Select
                      {...field}
                      input={<OutlinedInput />}
                      sx={{ flexGrow: 0.3 }}
                      onChange={(e: SelectChangeEvent) =>
                        field.onChange(e.target.value)
                      }
                    >
                      {Object.entries(CurrencyCode).map((c: any) => {
                        return (
                          <MenuItem key={c} value={c[0]}>
                            <Box
                              sx={{
                                display: 'flex',
                                gap: '5px',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  width: '25px',
                                  height: '25px',
                                  padding: '3px',
                                  backgroundColor: '#042938',
                                  alignContent: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '50%',
                                }}
                              >
                                <Typography
                                  fontWeight={500}
                                  style={{ color: 'white', margin: 0 }}
                                  fontSize={'0.8rem'}
                                >
                                  {CurrencySymbols[c[0] as CurrencyCode]}
                                </Typography>
                              </Box>
                              <Typography>{c[0]}</Typography>
                            </Box>
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </FormControl>
                )
              }}
            />
          </Box>
          <Typography>Billing Terms</Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Controller
                name="billing.billingTermTypeName"
                control={control}
                render={({ field }) => {
                  return (
                    <FormControl sx={{ display: 'flex' }}>
                      <RadioSwitch
                        {...field}
                        value={field.value || 'DaysFromInvoice'}
                        label={'Billing Term'}
                        options={[
                          {
                            value: 'DaysFromInvoice',
                            label: 'Days from Invoice',
                          },
                          { value: 'EndOfMonth', label: 'End of Month' },
                        ]}
                      />
                    </FormControl>
                  )
                }}
              />
            </Box>

            <Controller
              name="billing.billingTermValue"
              control={control}
              render={({ field }) => {
                const termTypeLabel =
                  billingTermTypeName === 'DaysFromInvoice'
                    ? 'Days from Invoice'
                    : billingTermTypeName === 'EndOfMonth'
                    ? 'Days from End of Month'
                    : ''
                return (
                  <FormControl sx={{ display: 'flex', width: '215px' }}>
                    <TextField
                      {...field}
                      sx={{ flexGrow: 0.5 }}
                      label={termTypeLabel}
                      type={'number'}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      required={
                        !!errors.billing &&
                        !!(errors.billing as any).billingTermValue
                      }
                      error={
                        !!errors.billing &&
                        !!(errors.billing as any).billingTermValue
                      }
                      helperText={
                        errors.billing &&
                        (errors.billing as any).billingTermValue?.message
                      }
                    />
                  </FormControl>
                )
              }}
            />
          </Box>

          <Typography>VAT Details</Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Controller
              name="billing.vatRegistered"
              control={control}
              render={({ field }) => (
                <>
                  <FormControlLabel
                    control={<Checkbox {...field} defaultChecked={false} />}
                    sx={{
                      display:
                        !!errors.address && !!(errors.address as any)
                          ? 'none'
                          : 'block',
                    }}
                    label="VAT Registered?"
                  />
                </>
              )}
            />

            {vatRegistered && (
              <Controller
                name="billing.vatNumber"
                control={control}
                render={({ field }) => {
                  return (
                    <TextField
                      {...field}
                      sx={{ flexGrow: 0.5 }}
                      disabled={!vatRegistered}
                      label={'VAT Number'}
                      InputLabelProps={{ shrink: !!vatRegistered }}
                      required={
                        !!errors.billing && !!(errors.billing as any).vatNumber
                      }
                      error={
                        !!errors.billing && !!(errors.billing as any).vatNumber
                      }
                      helperText={
                        errors.billing &&
                        (errors.billing as any).vatNumber?.message
                      }
                    />
                  )
                }}
              />
            )}
          </Box>
          <Typography>Banking Information</Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Controller
              name="billing.payment.accountHolderName"
              control={control}
              render={({ field }) => {
                return (
                  <TextField
                    {...field}
                    sx={{ flexGrow: 1 }}
                    label={'Account Holder Name'}
                    required={
                      !!errors.billing &&
                      typeof errors.billing === 'object' &&
                      'payment' in errors.billing &&
                      !!(errors.billing as any).payment?.accountHolderName
                    }
                    error={
                      !!errors.billing &&
                      typeof errors.billing === 'object' &&
                      'payment' in errors.billing &&
                      !!(errors.billing as any).payment?.accountHolderName
                    }
                    helperText={
                      errors.billing &&
                      typeof errors.billing === 'object' &&
                      'payment' in errors.billing &&
                      (errors.billing as any).payment?.accountHolderName
                        ?.message
                    }
                  />
                )
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: '20px' }}>
            <Controller
              name="billing.payment.sortCode"
              control={control}
              render={({ field }) => {
                const formatSortCode = (value: string) => {
                  return value
                    .replace(/\D/g, '')
                    .slice(0, 6)
                    .replace(/(\d{2})(\d{2})(\d{0,2})/, (m, p1, p2, p3) =>
                      p3 ? `${p1}-${p2}-${p3}` : p2 ? `${p1}-${p2}` : p1,
                    )
                }
                return (
                  <TextField
                    {...field}
                    sx={{ flexGrow: 0.3 }}
                    label={'Sort code'}
                    required={
                      !!errors.billing &&
                      !!(errors.billing as any).payment?.sortCode
                    }
                    error={
                      !!errors.billing &&
                      !!(errors.billing as any).payment?.sortCode
                    }
                    helperText={
                      errors.billing &&
                      (errors.billing as any).payment?.sortCode?.message
                    }
                    value={formatSortCode(field.value ?? '')}
                    onChange={(e) => {
                      const digits = e.target.value
                        .replace(/\D/g, '')
                        .slice(0, 6)
                      field.onChange(digits)
                    }}
                  />
                )
              }}
            />
            <Controller
              name="billing.payment.accountNumber"
              control={control}
              render={({ field }) => {
                return (
                  <TextField
                    {...field}
                    sx={{ flexGrow: 0.7 }}
                    label={'Account Number'}
                    required={
                      !!errors.billing &&
                      !!(errors.billing as any).payment?.accountNumber
                    }
                    error={
                      !!errors.billing &&
                      !!(errors.billing as any).payment?.accountNumber
                    }
                    helperText={
                      errors.billing &&
                      (errors.billing as any).payment?.accountNumber?.message
                    }
                  />
                )
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}
